'use client'

import {usePathname, useRouter} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {useState, useTransition} from 'react'
import {useToast} from '@/components/Toast'
import {api, ApiError} from '@/lib/api/client'
import type {Locale, Me} from '@/lib/api/types'
import {
  LOCALE_SWITCH_COOKIE,
  LOCALE_SWITCH_MAX_AGE_SECONDS,
  NEXT_INTL_COOKIE
} from '@/lib/request-headers'

/**
 * The one implementation of "switch the app's language" (FR-8 / T-I18N-01).
 *
 * Both switchers — the header's `LangSwitch` and the settings page's option
 * list — call this. They used to carry a copy each, and the copies had
 * already drifted.
 *
 * Two things it is careful about, both of which were wrong when the switch
 * was written twice:
 *
 *  1. **The account write does not block the switch.** `PUT /me/settings` is
 *     what makes the choice follow the student to another device; it has
 *     nothing to do with rendering this device in the new language. Awaiting
 *     it put a full API round trip in front of a navigation that was already
 *     going to cost one, so the button did nothing at all for the length of
 *     two round trips. The cookies below are what the next request reads, and
 *     they are set synchronously — `app/[locale]/(app)/layout.tsx` treats
 *     `LOCALE_SWITCH_COOKIE` as "a switch is in flight, do not redirect me
 *     back", which is precisely what makes navigating first safe.
 *
 *  2. **`replace`, not `push`.** A language switch is the same page in
 *     another language, not a place in the history. Pushing meant Back
 *     walked through every language the student had tried, landing on URLs
 *     whose locale no longer matches their account — so Back either bounced
 *     forward again or showed the old language at the new URL.
 *
 * `pending` is returned because the navigation is a server round trip: the
 * caller has to be able to say something is happening. `value` is the
 * optimistic selection, so the control acknowledges the click on the click
 * rather than a second later.
 */
export function useLocaleSwitch(locale: Locale) {
  const router = useRouter()
  const pathname = usePathname()
  const toast = useToast()
  const tErrors = useTranslations('errors')
  const [pending, startTransition] = useTransition()
  const [value, setValue] = useState<Locale>(locale)

  function switchTo(next: Locale) {
    if (next === locale || pending) return
    setValue(next)

    // Two cookies: next-intl reads the first for the landing page, and the
    // second is the app shell's evidence that this was a deliberate switch.
    const attributes = `path=/; max-age=${LOCALE_SWITCH_MAX_AGE_SECONDS}; samesite=lax`
    document.cookie = `${NEXT_INTL_COOKIE}=${next}; ${attributes}`
    document.cookie = `${LOCALE_SWITCH_COOKIE}=${next}; ${attributes}`

    const rest = pathname.split('/').slice(2).join('/')
    startTransition(() => router.replace(`/${next}${rest ? `/${rest}` : ''}`))

    // Deliberately not awaited, and deliberately not reverting on failure:
    // the switch has already happened in this browser and undoing it under
    // the student would be worse than the thing that went wrong. All a
    // failure costs is that the choice will not follow them to another
    // device until the next successful save, so it is worth a toast and
    // nothing more.
    void api.put<Me>('/me/settings', {locale: next}).catch((error) => {
      toast.show(tErrors(error instanceof ApiError ? error.messageKey : 'internal'), 'error')
    })
  }

  return {value, pending, switchTo}
}
